import {
  ChevronRight,
  CircleUserRound,
  ClipboardCheck,
  FileSearch2,
  IdCardLanyard,
  NotebookText,
  Plane,
  TowerControl,
} from '@/components/icons'
import { Link } from '@tanstack/react-router'
import { Fragment } from 'react'
import { AppLayout } from './AppLayout'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'
import { TopBarTitle } from './topbar/TopBarTitle'
import { hapticLight } from '@/lib/haptics'

interface MoreItem {
  icon: React.ReactNode
  title: string
  subtitle: string
  to: string
}

export function More() {
  const { isAdmin } = useIsAdmin()
  const { isPilot } = useIsPilot()

  const items: MoreItem[] = [
    {
      icon: <IdCardLanyard className="size-8 text-content-brand-bold" />,
      title: 'Crew Directory',
      subtitle: 'Profiles, roles & docs',
      to: isAdmin ? '/operations/crew-directory' : '/crew-directory',
    },
    {
      icon: <CircleUserRound className="size-8 text-content-brand-bold" />,
      title: 'PAX Directory',
      subtitle: 'Profiles and preferences',
      to: isAdmin ? '/operations/pax' : '/pax',
    },
    {
      icon: <FileSearch2 className="size-8 text-content-brand-bold" />,
      title: 'Documents',
      subtitle: 'SOPs, manuals & compliance',
      to: isAdmin ? '/operations/documents' : '/documents',
    },
    {
      icon: <Plane className="size-8 text-content-brand-bold" />,
      title: 'Aircraft',
      subtitle: 'Fleet status & documents',
      to: isAdmin ? '/operations/aircraft' : '/aircraft',
    },
    // Pilots and admins — company-wide technical log table; mirrors the
    // desktop sidebar and the /flights route guard (canView = isAdmin || isPilot).
    ...(isAdmin || isPilot
      ? [
          {
            icon: <NotebookText className="size-8 text-content-brand-bold" />,
            title: 'Flights',
            subtitle: 'All flights across the fleet',
            to: '/flights',
          },
        ]
      : []),
    {
      icon: <TowerControl className="size-8 text-content-brand-bold" />,
      title: 'Airport Directory',
      subtitle: 'Find airport details',
      to: '/airport-directory',
    },
    {
      icon: <ClipboardCheck className="size-8 text-content-brand-bold" />,
      title: 'Checklists',
      subtitle: 'Templates, tasks and submissions',
      to: '/tasks',
    },
  ]

  return (
    <AppLayout topBar={<TopBarTitle title="More" />} className="py-4">
      <div className="bg-surface-L1 border border-border-L1 rounded-2xl px-4">
        {items.map((item, index) => (
          <Fragment key={item.title}>
            {index > 0 && <div className="h-px bg-border-L1" />}
            <Link
              to={item.to}
              onClick={hapticLight}
              className="flex items-center justify-between py-4 active:opacity-60 transition-opacity"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-background-brand-subtle p-2 rounded-lg shrink-0">
                  {item.icon}
                </div>
                <div className="flex flex-col min-w-0">
                  <p className="font-semibold text-base text-content-primary-bold leading-6 tracking-[-0.5px]">
                    {item.title}
                  </p>
                  <p className="text-sm text-content-primary-subtle leading-5">
                    {item.subtitle}
                  </p>
                </div>
              </div>
              <ChevronRight className="size-4 text-content-primary-subtle shrink-0" />
            </Link>
          </Fragment>
        ))}
      </div>
    </AppLayout>
  )
}
